import { useRef } from "react";

import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { hideToast, showToast } from "app/actions/toast_actions";
import type { ExcelDownloadResult } from "app/components/React/ExcelButton/ExcelClientDownload";
import type { IToast } from "app/components/React/Toast/types";
import {
    exportToExcelToastThunk,
    type ExportExcelService,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

type Options = {
    mutationFn: (domainsOrCount: string[] | number) => Promise<ExcelDownloadResult>;
    toastService: ExportExcelService;
};

export const useLeadsExcelExportMutation = ({ mutationFn, toastService }: Options) => {
    const dispatch = useDispatch();

    const toastRef = useRef<IToast>(null);

    return useMutation({
        mutationFn,
        onMutate(domainsOrCount: string[] | number) {
            const count = is.number(domainsOrCount) ? domainsOrCount : domainsOrCount.length;

            toastRef.current = toastService.createToast(toastService.getExportingToastTitle(count));

            dispatch(
                showToast(
                    toastService.getToastComponent(toastRef.current.children as string),
                    "success",
                    toastRef.current.timestamp,
                ),
            );
        },
        async onSettled(data) {
            if (toastRef.current) {
                dispatch(hideToast(toastRef.current));
            }

            if (data?.success) {
                dispatch(exportToExcelToastThunk(toastService.getSuccessToastTitle()));

                await quotaQueryClient.invalidateExportQuota();
            } else {
                dispatch(exportToExcelToastThunk(toastService.getErrorToastTitle()));
            }
        },
    });
};
